import React, { useState } from "react";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import {
  Box,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Popover,
  Button,
  Typography,
  IconButton,
  Chip,
  Switch,
} from "@mui/material";
import CustomTypography from "../../globalStyles/fonts";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import { isMobile } from "../../globalStyles/mobile";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTheme } from "@mui/styles";
import EditIcon from "../../globalStyles/icons/editIcon";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import { formatDate } from "../../utils/formatDate";
import FilterModal from "../filterModal";
import TableHeader from "../table-header";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    padding: "6px",
    textAlign: "start",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: "4px",
    textAlign: "start",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--row-bg-odd)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
  },
  "&:lastChild td, &:lastChild th": {
    border: 0,
  },
}));

const RoleSidemenuTableCell = ({ x, handleEditClick, handleDeleteClick }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const handleActionClick = (action) => {
    handlePopoverClose();
    switch (action) {
      case "edit":
        handleEditClick(x);
        break;
      case "delete":
        handleDeleteClick(x);
        break;
      default:
        break;
    }
  };

  return (
    <Box>
      <IconButton
        aria-label="more actions"
        onClick={handlePopoverOpen}
        sx={{
          color: open ? "primary.main" : "inherit",
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={1}>
          <Button
            startIcon={<EditIcon />}
            onClick={() => handleActionClick("edit")}
            fullWidth
            sx={{ justifyContent: "flex-start" }}
          >
            Edit
          </Button>
          <Button
            startIcon={<DeleteIcon />}
            onClick={() => handleActionClick("delete")}
            fullWidth
            sx={{ justifyContent: "flex-start" }}
          >
            Delete
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

const RoleSidemenuList = ({
  data,
  onEditMapping,
  onDeleteMapping,
  onToggleAccessible,
  loading,
  filterData,
  onChangeFilter,
  currentPage,
  itemsPerPage,
}) => {
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [selectedFilterData, setSelectedFilterData] = useState({
    key: "",
    name: "",
    filter: [],
    sort: 0,
    id: "_id",
    displayKey: "name",
    fetchApi: () => {},
    loadMoreButton: false,
    options: [],
    searchKey: "",
    type: "",
  });
  const theme = useTheme();
  const defaultColor = "#D13438";
  const primary_color = theme.palette?.primary?.main || defaultColor;

  const startIndex = (currentPage || 0) * (itemsPerPage || 15);

  const handleDeleteClick = (x) => {
    setSelectedMapping(x);
    setOpenConfirmation(true);
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    setLoadingAction(true);
    try {
      await onDeleteMapping(selectedMapping._id);
      setOpenConfirmation(false);
      setSelectedMapping(null);
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleEditClick = (x) => {
    setSelectedMapping(x);
    onEditMapping(x);
  };

  const handleOpenFilter = (event, filterKey) => {
    setAnchorElFilter(event.currentTarget);
    setOpenFilterModal(true);
    setSelectedFilterData(
      filterData?.columns?.find((x) => x?.key === filterKey),
    );
  };

  const handleCloseFilter = () => {
    setAnchorElFilter(null);
    setOpenFilterModal(false);
  };

  const handleSaveFilter = (
    selectedOptions,
    filterKey,
    sortOrder,
    searchKey,
  ) => {
    onChangeFilter({
      key: filterKey,
      filter: selectedOptions,
      sort: sortOrder,
      searchKey: searchKey,
    });
    handleCloseFilter();
  };

  const TableSchema = [
    {
      key: "sl_no",
      title: "SL NO",
      render: (item, index) => (
        <Typography
          fontSize="12px"
          color="var(--text-secondary)"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {startIndex + index + 1}
        </Typography>
      ),
    },
    {
      key: "sidemenu",
      title: "Menu Item",
      handleOpenFilter: handleOpenFilter,
      render: (item) => (
        <Typography
          fontSize="12px"
          color="var(--text-secondary)"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {item.sidemenu?.title}
        </Typography>
      ),
    },
    {
      key: "sidemenu_order",
      title: "Order",
      render: (item) => (
        <Typography
          fontSize="12px"
          color="var(--text-secondary)"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {item.sidemenu?.order}
        </Typography>
      ),
    },
    {
      key: "system_generated",
      title: "System Generated",
      handleOpenFilter: handleOpenFilter,
      render: (item) => (
        <Chip
          label={item.sidemenu?.system_generated ? "Yes" : "No"}
          color={item.sidemenu?.system_generated ? "info" : "default"}
          size="small"
        />
      ),
    },
    {
      key: "createdAt",
      title: "Created At",
      handleOpenFilter: handleOpenFilter,
      render: (item) => formatDate(item.createdAt),
    },
    {
      key: "accessible",
      title: "Accessible",
      render: (item) => (
        <Tooltip
          title={
            item.accessible !== false
              ? "Active in sidebar"
              : "Read-only in sidebar"
          }
        >
          <Switch
            checked={item.accessible !== false}
            size="small"
            onChange={(e) => onToggleAccessible?.(item, e.target.checked)}
          />
        </Tooltip>
      ),
    },
    {
      key: "action",
      title: "",
      render: (item) => (
        <RoleSidemenuTableCell
          x={item}
          handleEditClick={handleEditClick}
          handleDeleteClick={handleDeleteClick}
        />
      ),
    },
  ];

  const renderListView = () => (
    <TableContainer
      sx={{
        maxHeight: "calc(100vh - 300px)",
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#c1c1c1",
          borderRadius: "3px",
        },
        scrollbarWidth: "thin",
        scrollbarColor: "#c1c1c1 #f1f1f1",
      }}
    >
      <Table stickyHeader aria-label="sticky table">
        <TableHead>
          <StyledTableRow>
            {TableSchema.map((x) => (
              <TableHeader
                key={x.key}
                filterData={filterData}
                filterKey={x.key}
                handleOpenFilter={x.handleOpenFilter}
                title={x.title}
              />
            ))}
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {data?.map((row, index) => (
            <StyledTableRow key={row._id}>
              {TableSchema.map((x) => (
                <StyledTableCell key={x.key}>
                  {x.render ? (
                    x.render(row, index)
                  ) : (
                    <Typography
                      fontSize="12px"
                      color="var(--text-secondary)"
                      fontWeight={400}
                      fontFamily={"Poppins"}
                    >
                      {row[x.displayKey]}
                    </Typography>
                  )}
                </StyledTableCell>
              ))}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <div>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      ) : (
        <div
          style={{
            overflowX: "auto",
            msOverflowStyle: "none",
            "&::WebkitScrollbar": { width: 0 },
          }}
        >
          {renderListView()}
        </div>
      )}

      <Popover
        open={Boolean(anchorElFilter)}
        anchorEl={anchorElFilter}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableScrollLock={true}
      >
        <FilterModal
          open={openFilterModal}
          onClose={handleCloseFilter}
          options={selectedFilterData?.options}
          selectedValues={selectedFilterData?.filter}
          onSave={handleSaveFilter}
          currentSortOrder={selectedFilterData?.sort}
          filterKey={selectedFilterData?.key}
          hasMore={selectedFilterData?.loadMoreButton}
          displayKey={selectedFilterData?.displayKey}
          id={selectedFilterData?.id}
          fetchApi={selectedFilterData?.fetchApi}
          searchKey={selectedFilterData?.searchKey}
          type={selectedFilterData?.type}
        />
      </Popover>

      <DeleteConfirmationModal
        title={`Are you sure you want to delete this role-sidemenu mapping?`}
        open={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleDelete={confirmDelete}
      />
    </div>
  );
};

export default RoleSidemenuList;
